import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { FaFilter } from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';

const SelectionTable = ({
  items,
  selectedItems,
  handleCheckboxChange,
  handleSelectAll,
  allSelected,
  columns,
  itemIdKey,
  renderRow,
  filteredItems = items,
  onFilterChange = () => {},
  onEditItem = () => {},
  actionColumnLabel = "Edit",
  showActionColumn = true
}) => {
  const [activeFilter, setActiveFilter] = useState(null);
  const [filters, setFilters] = useState({});
  const filterDropdownRef = useRef(null);
  const filterButtonRefs = useRef({});
  const [searchTerm, setSearchTerm] = useState("");
  
  const displayedItems = React.useMemo(() => {
    if (Object.keys(filters).length === 0) return filteredItems;
    return filteredItems.filter(item => {
      return Object.entries(filters).every(([columnKey, allowedValues]) => {
        if (!allowedValues || allowedValues.length === 0) return true;
        return allowedValues.includes(item[columnKey]);
      });
    });
  }, [filteredItems, filters]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setActiveFilter(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  const getUniqueValues = (columnKey) => {
    const unique = [...new Set(items.map(item => item[columnKey]).filter(val => val !== undefined && val !== null))];
    return unique.sort();
  };

  const toggleFilter = (columnKey) => setActiveFilter(activeFilter === columnKey ? null : columnKey);

  const handleFilterSelect = (columnKey, value, isChecked) => {
    const currentFilters = { ...filters };
    if (!currentFilters[columnKey]) {
      currentFilters[columnKey] = [];
    }
    if (isChecked) {
      currentFilters[columnKey].push(value);
    } else {
      currentFilters[columnKey] = currentFilters[columnKey].filter(v => v !== value);
    }
    if (currentFilters[columnKey].length === 0) {
      delete currentFilters[columnKey];
    }
    setFilters(currentFilters);
    onFilterChange(currentFilters);
  };
  
  const clearColumnFilter = (columnKey) => {
    const newFilters = { ...filters };
    delete newFilters[columnKey];
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  useEffect(() => {
    setSearchTerm("");
  }, [activeFilter]);
  
  const renderFilterDropdown = (columnKey) => {
    if (activeFilter !== columnKey) return null;
    const buttonRect = filterButtonRefs.current[columnKey]?.getBoundingClientRect();
    if (!buttonRect) return null;

    const filteredValues = getUniqueValues(columnKey).filter(value =>
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return ReactDOM.createPortal(
      <div
        ref={filterDropdownRef}
        className="fixed z-20 bg-white rounded-lg shadow-2xl border w-64"
        style={{
          top: `${buttonRect.bottom + 8}px`,
          left: `${Math.max(buttonRect.left - 220, 10)}px`,
        }}
      >
        <div className="p-2 border-b">
          <input type="text" placeholder="Search values..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onClick={e => e.stopPropagation()} className="w-full px-2 py-1 border rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" />
        </div>
        <div className="max-h-60 overflow-y-auto p-2">
          {filteredValues.map(value => (
            <label key={value} className="flex items-center space-x-2 p-1.5 rounded-md hover:bg-gray-100 cursor-pointer">
              <input type="checkbox" checked={filters[columnKey]?.includes(value) || false} onChange={(e) => handleFilterSelect(columnKey, value, e.target.checked)} onClick={(e) => e.stopPropagation()} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
              <span className="text-sm text-gray-700 select-none">{value}</span>
            </label>
          ))}
        </div>
        <div className="p-2 border-t flex justify-end">
          <button onClick={() => clearColumnFilter(columnKey)} className="text-sm text-blue-600 hover:underline">Clear Filter</button>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="overflow-x-auto align-middle inline-block min-w-full">
      <div className="shadow border-b border-gray-200 sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3" style={{ width: '5%' }}>
                <input type="checkbox" checked={allSelected} onChange={handleSelectAll} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
              </th>
              {columns.map((column) => (
                <th key={column.key} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: column.width }}>
                  <div className="flex items-center gap-2">
                    <span>{column.label}</span>
                    <div className="relative">
                      <button ref={el => filterButtonRefs.current[column.key] = el} onClick={(e) => { e.stopPropagation(); toggleFilter(column.key); }} className={`p-1 rounded-full ${filters[column.key] ? 'text-blue-600 bg-blue-100' : 'text-gray-400 hover:bg-gray-200'}`}>
                        <FaFilter size={12} />
                      </button>
                      {renderFilterDropdown(column.key)}
                    </div>
                  </div>
                </th>
              ))}
              {showActionColumn && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '8%' }}>{actionColumnLabel}</th>}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayedItems.map((item) => {
              const itemId = item[itemIdKey];
              return (
                <tr key={itemId} onClick={() => handleCheckboxChange(itemId)} className={`cursor-pointer ${selectedItems.includes(itemId) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                  <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedItems.includes(itemId)} onChange={(e) => handleCheckboxChange(itemId, e)} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                  </td>
                  {renderRow(item)}
                  {showActionColumn && (
                    <td className="px-6 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => onEditItem(item)} className="p-2 text-gray-500 rounded-full hover:bg-gray-200 hover:text-blue-600">
                        <MdEdit size={18} />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SelectionTable;